import { Formik, Field, Form, FieldProps, ErrorMessage } from 'formik';
import MaskedInput from 'react-text-mask';
import * as Yup from 'yup';
import * as ROUTES from '../../_constants/routes';
import { useDispatch, useSelector } from 'react-redux';
import FormField from '../../_components/FormField';
import PageHeader from '../../_components/PageHeader';
import LinkNavigation from '../../_components/LinkNavigation';
import { setRegisterLoading } from '../../_actions/register.actions';
import ButtonSpinner from '../../_components/ButtonSpinner/index';
import { isEmpty } from 'lodash';
import Text from '../../_components/Text/index';
import { NextSignupFlowMessageRequest } from '../../_types/nextSignupFlowMessage';
import { nextSignupFlowMessage } from '../../_services/nextSignupFlowMessage.service';
import { register } from '../../_services/register.service';
import Loader from '../../_components/Loader/Loader';
import mlbLogo from '../../_assets/mlbLogo.png';
import axsLogo from '../../_assets/axsLogo.png';
import { registerSelector, upcomingEventsSelector } from '../../_selectors';
import ScrollToTop from '../../_components/ScrollToTop';
import { Fragment, useState } from 'react';
import { useSetInitial } from '../../_hooks/useSetInitial';
import { RegisterRequest } from '../../_types/register';
import { useIsTextBuy } from '../../_hooks/useIsTextBuy';
import { useSalesChannel } from '../../_hooks/useSalesChannel';
import { emailValidationSchema } from '../../_utils/emailValidationSchema';
import { FaqIconModal } from '../../_components/FaqIconModal/FaqIconModal';
import UpComingMatches from '../../_components/UpComingMatches';
import { PageHeaderBanner } from '../../_components/PageHeaderBanner';
import ReCAPTCHA from 'react-google-recaptcha';
import { FaCheckCircle } from 'react-icons/fa';

const usPhoneMaskWithCode = [
    '(',
    '+',
    '1',
    ')',
    ' ',
    /\d/,
    /\d/,
    /\d/,
    '-',
    /\d/,
    /\d/,
    /\d/,
    '-',
    /\d/,
    /\d/,
    /\d/,
    /\d/,
];
const usPhoneMask = ['(', '+', /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/];
const isPrefilledPhoneNumber = !!sessionStorage.getItem('serviceToken');
const initialPhoneValue = isPrefilledPhoneNumber ? `+${sessionStorage.getItem('serviceToken')}` : '';
const initialEmailValue = sessionStorage.getItem('email') || '';

const SignUpPage: React.FC = () => {
    useSetInitial();
    const dispatch = useDispatch();

    const { loading, data: registerData, error } = useSelector(registerSelector);
    const { data: eventsData } = useSelector(upcomingEventsSelector);
    const [graduationYear, setGraduationYear] = useState(0);
    const [graduationMonth, setGraduationMonth] = useState(0);

    const isTextBuy = useIsTextBuy();
    const {
        textBuyUI,
        salesChannelId,
        sourceSpecificSignupSubTitle,
        sourceSpecificBackground,
        salesChannelName,
        salesChannelProvider,
        salesChannelConfig,
    } = useSalesChannel();

    const channel = sessionStorage.getItem('channel');
    const source = sessionStorage.getItem('source');
    const isStudentRush = source === 'studentrush';
    const isTeacherProgram = source === 'teacher';

    const isAXSOrVeritixProvider = salesChannelProvider === 'axs' || salesChannelProvider === 'veritix';
    const shouldDisplayMarketingCheckbox = salesChannelConfig?.marketingCheckboxOnSignup;
    const shouldDisplayGraduationInputs = isStudentRush && [55, 108, 71].includes(salesChannelId);
    const isMLBProvider = salesChannelProvider === 'mlb';
    const ROCKETS_ID = 38;
    const isRocket = salesChannelId === ROCKETS_ID;

    // Check inventory if all passes are soldOut
    const isSoldOut = false;

    let title, subTitle;

    title = `Sign up for Ballpark Pass`;
    subTitle = registerData
        ? isSoldOut
            ? 'The 2021 Ballpark Pass has SOLD OUT. You are on the waitlist to be notified when the next Ballpark Pass becomes available. Waitlist holders will receive special offers and purchase opportunities by email throughout the 2021 season.'
            : 'Awesome! We’ve sent a message to your phone with the next steps.'
        : 'Get exclusive access to tickets with the all new Giants Ballpark pass.';

    if (isTextBuy) {
        title = isStudentRush
            ? 'Student Rush'
            : isTeacherProgram
            ? 'Teacher Rush Text-to-Buy'
            : 'Sign up for Text-to-Buy';
        subTitle = registerData
            ? 'Awesome! We’ve sent a message to your phone with the next steps.'
            : sourceSpecificSignupSubTitle || `We will text you when seats are available.`;
    }

    if (!textBuyUI || !salesChannelId) {
        return <Loader message="Could not fetch salesChannels!" />;
    }
    const isGraduationDateValid = () => {
        const currentYear = new Date().getFullYear();
        const currentMonth = new Date().getMonth() + 1; // Convert to 1-based month

        if (graduationYear === 0 || graduationMonth === 0 || isNaN(graduationYear) || isNaN(graduationMonth)) {
            return false;
        }

        if (graduationYear < currentYear || graduationYear > currentYear + 10) {
            return false;
        }

        if (graduationMonth < 1 || graduationMonth > 12) {
            return false;
        }

        // If graduation year is the current year, ensure the month is in the future
        if (graduationYear === currentYear && graduationMonth <= currentMonth) {
            return false;
        }

        return true;
    };

    const resendConfirmation = () => {
        if (registerData) {
            const signupFlowData: NextSignupFlowMessageRequest = {
                userId: registerData.id,
                messageChannelType: sessionStorage.getItem('channel') || 'twilio',
            };

            dispatch(nextSignupFlowMessage(signupFlowData, textBuyUI.salesChannelId));
        }
    };

    const validationSchema = Yup.object({
        firstName: Yup.string().required('Required'),
        lastName: Yup.string().required('Required'),
        phone: Yup.string()
            .test('len', 'Enter valid phone number.', (val) => {
                const val_length_without_dashes = val?.replace(/-|_/g, '').length;
                return isPrefilledPhoneNumber || val_length_without_dashes === 15;
            })
            .required('Required'),
        email: emailValidationSchema(initialEmailValue),
        recaptcha: Yup.string().nullable().required('Recaptcha check required'),
        opted_in_informational: Yup.bool().oneOf(
            isAXSOrVeritixProvider && shouldDisplayMarketingCheckbox ? [true] : [true, false],
            'Required!',
        ),
    });

    const formattedError = (error: string) => {
        if (
            error.includes('No salesChannel') ||
            error.includes('No available message channel') ||
            error.includes('Failed to update scmc')
        ) {
            return 'Registration Failed. Please make sure you followed a correct link.';
        }

        return error;
    };
    return (
        <div
            className="w-100 flex min-h-screen fw-container bg-center bg-cover md:py-4"
            style={{
                backgroundImage: `url(${
                    sourceSpecificBackground ? sourceSpecificBackground : textBuyUI?.signupBackgroundUrl
                })`,
            }}
        >
            <div className="container w-full md:my-auto md:max-w-xl px-0 mx-auto md:border-2 border-white md:rounded-xl">
                <PageHeaderBanner />
                <div className="m-3">
                    <ScrollToTop />
                    <PageHeader checkIcon={!!registerData} title={title} subTitle={subTitle} position="center" />
                    {!isEmpty(error) && (
                        <Text message={formattedError(error?.body?.message || 'Registration failed!')} type="error" />
                    )}
                    {registerData && (
                        <button
                            className="btn btn-primary mt-3 col-12"
                            disabled={sessionStorage.getItem('channel') === 'attentive'}
                            onClick={resendConfirmation}
                            type="submit"
                        >
                            Resend Confirmation
                        </button>
                    )}
                    {!registerData && (
                        <Formik
                            initialValues={{
                                firstName: '',
                                lastName: '',
                                phone: initialPhoneValue,
                                email: initialEmailValue,
                                recaptcha: '',
                                salesChannelId: textBuyUI.salesChannelId,
                                shortCodeSignup: false,
                                serviceToken: '',
                                optedIn: false,
                                countryCode: '1',
                                opted_in_informational: false,
                                referrer: sessionStorage.getItem('referrer') || '',
                                source: source || (isRocket ? 'general' : 'WEB'),
                                campaign: sessionStorage.getItem('campaign') ?? 'season24-25',
                                messageChannelType: sessionStorage.getItem('channel') || 'twilio',
                            }}
                            validationSchema={validationSchema}
                            onSubmit={(values) => {
                                const phoneNumber = values.phone.replace(/[ ()-]/gm, '');
                                // This is where you could send the submitted values to the backend
                                dispatch(setRegisterLoading());
                                const data: RegisterRequest = {
                                    ...values,
                                    phone: phoneNumber,
                                    serviceToken: phoneNumber,
                                    graduationMonth: graduationMonth,
                                    graduationYear: graduationYear,
                                };
                                dispatch(register(data));
                            }}
                        >
                            {({ isValid, setFieldValue }) => {
                                return (
                                    <Form className="form-container">
                                        <FormField name="firstName" type="text" label="First Name" />
                                        <FormField name="lastName" type="text" label="Last Name" />
                                        <FormField name="phone" type="text" label="Mobile Phone">
                                            {({ field }: FieldProps<string>) => {
                                                return (
                                                    <MaskedInput
                                                        {...field}
                                                        mask={
                                                            isPrefilledPhoneNumber ? usPhoneMask : usPhoneMaskWithCode
                                                        }
                                                        id="phone"
                                                        type="text"
                                                        className={'form-control'}
                                                        disabled={isPrefilledPhoneNumber}
                                                    />
                                                );
                                            }}
                                        </FormField>
                                        <FormField name="email" type="email" label="Email">
                                            {({ field }: FieldProps<string>) => {
                                                return (
                                                    <input
                                                        {...field}
                                                        id="email"
                                                        type="email"
                                                        className={'form-control'}
                                                        disabled={!!initialEmailValue}
                                                    />
                                                );
                                            }}
                                        </FormField>
                                        {shouldDisplayGraduationInputs && (
                                            <div>
                                                <div className="row">
                                                    <div className="text-sm font-weight-bold col-12">
                                                        Expected Graduation Date
                                                    </div>
                                                </div>
                                                <div className="my-2 row flex-nowrap">
                                                    <div className="col-1">
                                                        <FaCheckCircle
                                                            color={isGraduationDateValid() ? 'green' : 'gray'}
                                                            size={24}
                                                            style={{ zIndex: 10 }}
                                                        />
                                                    </div>
                                                    <div className="col-5">
                                                        <select
                                                            id="graduationMonth"
                                                            className="form-control md:w-14"
                                                            placeholder="Month"
                                                            onChange={(event) => {
                                                                setGraduationMonth(parseInt(event.currentTarget.value));
                                                            }}
                                                        >
                                                            <option value="">Month</option>
                                                            {[
                                                                'January',
                                                                'February',
                                                                'March',
                                                                'April',
                                                                'May',
                                                                'June',
                                                                'July',
                                                                'August',
                                                                'September',
                                                                'October',
                                                                'November',
                                                                'December',
                                                            ].map((monthName, index) => (
                                                                <option key={index + 1} value={index + 1}>
                                                                    {monthName}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                    <div className="col-6">
                                                        <select
                                                            id="graduationYear"
                                                            className="form-control md:w-14"
                                                            placeholder="Year"
                                                            onChange={(event) => {
                                                                setGraduationYear(parseInt(event.currentTarget.value));
                                                            }}
                                                        >
                                                            <option>Year</option>
                                                            {Array.from(
                                                                { length: 10 },
                                                                (_, i) => new Date().getFullYear() + i,
                                                            ).map((year) => (
                                                                <option key={year} value={year}>
                                                                    {year}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                </div>
                                                <hr />
                                            </div>
                                        )}
                                        <Field className="form-control" name="campaign" type="hidden" />
                                        <Field className="form-control" name="countryCode" type="hidden" />
                                        <Field className="form-control" name="messageChannelType" type="hidden" />
                                        <Field className="form-control" name="optedIn" type="hidden" />
                                        <Field className="form-control" name="salesChannelId" type="hidden" />
                                        <Field className="form-control" name="serviceToken" type="hidden" />
                                        <Field
                                            className="form-control"
                                            name="shortCodeSignup"
                                            value="phone"
                                            type="hidden"
                                        />
                                        <Field className="form-control" name="source" type="hidden" />
                                        {salesChannelConfig && (
                                            <Fragment>
                                                {isAXSOrVeritixProvider && (
                                                    <div className="provider">
                                                        <span className="provider__logo">
                                                            <img
                                                                src={axsLogo}
                                                                className="mt-2 provider__logo--sizeAxs"
                                                                alt="AXS logo"
                                                            />
                                                        </span>
                                                        <span className="text-small provider__text">
                                                            {isStudentRush
                                                                ? `If you have an AXS account, please use your .edu email address linked to your AXS account. Only .edu email addresses are valid for Student program.`
                                                                : 'If you have an AXS account, please use your email address linked to your AXS account.'}
                                                        </span>
                                                    </div>
                                                )}
                                                {isMLBProvider && (
                                                    <div className="provider">
                                                        <span className="provider__logo">
                                                            <img
                                                                src={mlbLogo}
                                                                className="mt-2 provider__logo--size"
                                                                alt="MLB logo"
                                                            />
                                                        </span>
                                                        <span className="text-small provider__text">
                                                            If you have an MLB account, please use your email address
                                                            linked to your MLB account.
                                                        </span>
                                                    </div>
                                                )}
                                                {shouldDisplayMarketingCheckbox && (
                                                    <>
                                                        <br />
                                                        <ErrorMessage
                                                            className="field-error-feedback"
                                                            component="div"
                                                            name={'opted_in_informational'}
                                                        />
                                                        <Field
                                                            type="checkbox"
                                                            name="opted_in_informational"
                                                            id="opted_in_informational"
                                                            className="mr-1"
                                                        />
                                                        <label
                                                            htmlFor="opted_in_informational"
                                                            className="d-inline font-medium text-sm"
                                                        >
                                                            {' '}
                                                            I want to receive marketing/promotional text messages about
                                                            ticket offers.
                                                        </label>
                                                    </>
                                                )}

                                                <ReCAPTCHA
                                                    sitekey={process.env.REACT_APP_GOOGLE_CAPTCHA!}
                                                    onChange={(token) => {
                                                        setFieldValue('recaptcha', token);
                                                    }}
                                                    size="normal"
                                                    badge="inline"
                                                    className="form-group mt-2 mb-0 g-recaptcha"
                                                />
                                                <ErrorMessage
                                                    name="recaptcha"
                                                    component="div"
                                                    className="field-error-feedback mt-0"
                                                />

                                                <button
                                                    className="btn btn-primary mt-1 col-12"
                                                    type="submit"
                                                    disabled={
                                                        !isValid ||
                                                        loading ||
                                                        (shouldDisplayGraduationInputs && !isGraduationDateValid())
                                                    }
                                                >
                                                    <ButtonSpinner show={loading} buttonLabel={'Sign up'} />
                                                </button>
                                                <p className="text-center text--state-link">
                                                    <FaqIconModal />
                                                    <span className="text-small">{'Already have an account? '}</span>
                                                    <LinkNavigation label="Sign In" path={ROUTES.LOGIN} />
                                                </p>
                                                <hr />
                                                <div className="text-extra-small mb-3">
                                                    By clicking Sign Up, you agree to the{' '}
                                                    {channel === 'attentive' ? (
                                                        <>
                                                            <a href={'https://attnl.tv/t/hbQ/r/US'} target="blank">
                                                                Terms
                                                            </a>{' '}
                                                            and{' '}
                                                            <a href={'https://attnl.tv/p/hbQ'} target="blank">
                                                                Privacy
                                                            </a>
                                                        </>
                                                    ) : (
                                                        <>
                                                            {salesChannelName}{' '}
                                                            <a href={salesChannelConfig.privacyPolicy} target="blank">
                                                                Privacy Policy
                                                            </a>{' '}
                                                            and{' '}
                                                            <a href={salesChannelConfig.termsOfUse} target="blank">
                                                                Terms of use
                                                            </a>
                                                        </>
                                                    )}
                                                    {', '}
                                                    {isAXSOrVeritixProvider && (
                                                        <>
                                                            {' AXS '}
                                                            <a
                                                                href={salesChannelConfig.axsPrivacyPolicy}
                                                                target="blank"
                                                            >
                                                                Privacy Policy
                                                            </a>{' '}
                                                            and{' '}
                                                            <a href={salesChannelConfig.axsTermsOfUse} target="blank">
                                                                Terms of use
                                                            </a>
                                                            {', '}
                                                        </>
                                                    )}
                                                    and the Pogoseat{' '}
                                                    <a
                                                        href="https://www.pogoseat.com/legal/privacy-policy"
                                                        target="blank"
                                                    >
                                                        Privacy Policy
                                                    </a>{' '}
                                                    and{' '}
                                                    <a
                                                        href="https://pogoseat.com/legal/terms-and-conditions"
                                                        target="blank"
                                                    >
                                                        {' '}
                                                        Terms and Conditions
                                                    </a>
                                                    .
                                                    {isRocket && (
                                                        <>
                                                            <br />
                                                            <br />
                                                            The Rockets allow fans to utilize AXS/FlashSeats and
                                                            Rockets.com as a secondary marketplace to sell and transfer
                                                            tickets. Tickets purchased via text CANNOT be resold or
                                                            transferred. Any other resale or attempted resale without
                                                            consent of the Rockets is grounds for seizure or
                                                            cancellation without refund or other compensation.
                                                        </>
                                                    )}
                                                    <br /> <br />
                                                    {channel === 'attentive' ? (
                                                        <>
                                                            As described further in the Terms, you agree to receive
                                                            recurring automated promotional and personalized marketing
                                                            text messages (e.g. cart reminders) from the{' '}
                                                            {salesChannelName} at the cell number used when signing up.
                                                            Consent is not a condition of any purchase. Reply HELP for
                                                            help and STOP to cancel. Msg frequency varies. Msg and data
                                                            rates may apply.
                                                        </>
                                                    ) : (
                                                        <>
                                                            As described further in the Terms, you agree that the{' '}
                                                            {salesChannelName}, and its service providers Pogoseat
                                                            {channel === 'attentive' ? ', Attentive' : ''}
                                                            {salesChannelProvider
                                                                ? ` and ${
                                                                      isAXSOrVeritixProvider
                                                                          ? 'AXS'
                                                                          : salesChannelProvider?.toUpperCase()
                                                                  }, `
                                                                : ','}{' '}
                                                            may send you automated text messages with offers and
                                                            promotions relating to tickets at {salesChannelName}.
                                                        </>
                                                    )}
                                                    <hr />
                                                    {channel !== 'attentive' && (
                                                        <i>
                                                            Text messages are sent by Pogoseat. Message and data rates
                                                            may apply. Messages are recurring and frequency varies.
                                                            Reply STOP to opt out or HELP for help. Consent is not a
                                                            condition of purchase.
                                                        </i>
                                                    )}
                                                </div>
                                            </Fragment>
                                        )}
                                    </Form>
                                );
                            }}
                        </Formik>
                    )}

                    {!isTextBuy && <UpComingMatches events={eventsData} />}
                </div>
            </div>
        </div>
    );
};

export default SignUpPage;
